/**
 * Energy Losses Graphic Component
 * Side-by-side diagram showing energy losses in older vs modern systems
 * Static diagram, not animation
 */

interface EnergyLossesGraphicProps {
  width?: number;
  height?: number;
}

export function EnergyLossesGraphic({ width = 560, height = 280 }: EnergyLossesGraphicProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 560 280"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', margin: '0 auto' }}
    >
      {/* Left side: Older system */}
      <g>
        {/* Title */}
        <text x="140" y="30" textAnchor="middle" fontSize="18" fontWeight="600" fill="#1e293b">
          Older system
        </text>

        {/* Fuel input arrow */}
        <path d="M 20 70 L 80 70" stroke="#475569" strokeWidth="4" markerEnd="url(#arrowhead)" />
        <text x="50" y="62" textAnchor="middle" fontSize="12" fill="#475569">Fuel in</text>

        {/* System box */}
        <rect x="80" y="50" width="120" height="60" rx="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="2" />
        <text x="140" y="85" textAnchor="middle" fontSize="14" fontWeight="600" fill="#1e293b">System</text>

        {/* Red leak arrows - multiple losses */}
        {/* Incomplete burn loss */}
        <path d="M 120 50 L 120 20" stroke="#dc2626" strokeWidth="3" strokeDasharray="4,4" markerEnd="url(#arrowhead-red)" />
        <text x="125" y="35" fontSize="11" fill="#dc2626">Incomplete</text>
        <text x="125" y="47" fontSize="11" fill="#dc2626">burn</text>

        {/* Flue loss */}
        <path d="M 160 50 L 160 20" stroke="#dc2626" strokeWidth="3" strokeDasharray="4,4" markerEnd="url(#arrowhead-red)" />
        <text x="165" y="35" fontSize="11" fill="#dc2626">Flue</text>
        <text x="165" y="47" fontSize="11" fill="#dc2626">loss</text>

        {/* Cycling loss */}
        <path d="M 140 110 L 140 140" stroke="#dc2626" strokeWidth="3" strokeDasharray="4,4" markerEnd="url(#arrowhead-red)" />
        <text x="145" y="125" fontSize="11" fill="#dc2626">Cycling</text>
        <text x="145" y="137" fontSize="11" fill="#dc2626">loss</text>

        {/* Delivered heat arrow (smaller) */}
        <path d="M 200 80 L 260 80" stroke="#64748b" strokeWidth="3" markerEnd="url(#arrowhead-gray)" />
        <text x="230" y="72" textAnchor="middle" fontSize="11" fill="#64748b">Delivered</text>
        <text x="230" y="95" textAnchor="middle" fontSize="11" fill="#64748b">heat</text>
      </g>

      {/* Right side: Modern system */}
      <g transform="translate(280, 0)">
        {/* Title */}
        <text x="140" y="30" textAnchor="middle" fontSize="18" fontWeight="600" fill="#1e293b">
          Modern system
        </text>

        {/* Fuel input arrow */}
        <path d="M 20 70 L 80 70" stroke="#475569" strokeWidth="4" markerEnd="url(#arrowhead)" />
        <text x="50" y="62" textAnchor="middle" fontSize="12" fill="#475569">Fuel in</text>

        {/* System box */}
        <rect x="80" y="50" width="120" height="60" rx="4" fill="#d1fae5" stroke="#10b981" strokeWidth="2" />
        <text x="140" y="85" textAnchor="middle" fontSize="14" fontWeight="600" fill="#1e293b">System</text>

        {/* Smaller amber leak arrows - still show some losses */}
        <path d="M 140 50 L 140 30" stroke="#f59e0b" strokeWidth="2" strokeDasharray="3,3" markerEnd="url(#arrowhead-amber)" />
        <text x="145" y="40" fontSize="10" fill="#f59e0b">Small</text>
        <text x="145" y="50" fontSize="10" fill="#f59e0b">losses</text>

        {/* Delivered heat arrow (larger) */}
        <path d="M 200 80 L 260 80" stroke="#10b981" strokeWidth="5" markerEnd="url(#arrowhead-green)" />
        <text x="230" y="72" textAnchor="middle" fontSize="11" fill="#10b981">Delivered</text>
        <text x="230" y="95" textAnchor="middle" fontSize="11" fill="#10b981">heat</text>
      </g>

      {/* Arrow markers */}
      <defs>
        <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="#475569" />
        </marker>
        <marker id="arrowhead-red" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="#dc2626" />
        </marker>
        <marker id="arrowhead-gray" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="#64748b" />
        </marker>
        <marker id="arrowhead-amber" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="#f59e0b" />
        </marker>
        <marker id="arrowhead-green" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="#10b981" />
        </marker>
      </defs>

      {/* Caption */}
      <text x="280" y="270" textAnchor="middle" fontSize="12" fontStyle="italic" fill="#64748b">
        Even modern systems still lose some energy — no system is perfect.
      </text>
    </svg>
  );
}
